'use client';
import { useState } from 'react';
import { SmartLink } from '../../SmartLink';
import { useT } from '../../../lib/i18n';
import type { ProductDetail } from '../../../lib/site';
import { toast } from '../../Toast';
import { platformLabel } from '../labels';
import { blockedLabel, buyable, discountShort, priceLines, statusLabel, won } from '../price';
import { BenefitNotices } from './BenefitNotice';
import { estimate, promotedTargets, targetLine, type CartLine } from './benefitEstimate';
import { BenefitPicks } from './BenefitPicks';
import { useRolling } from './useRolling';
import { Working } from '../../Working';
import { cartChanged } from '../../cartCount';

/**
 * 구매 박스 — 가이드 `pd-buybox`(왼쪽 고를 것 / 오른쪽 합계·버튼).
 *
 * 고를 것이 두 묶음이다 —
 *  ① **강좌**: 단가 라디오(공용·PC·모바일). 단가 세분화를 안 쓰는 마켓은 한 줄뿐이라
 *     고를 것이 없지만 **줄은 그린다**(무엇을 사는지 보여야 한다).
 *  ② **교재**: 구성에 딸린 교재. 체크박스라 여러 개를 함께 담을 수 있다.
 *     기본은 **꺼짐** — 켜 두면 회원이 모르고 함께 사게 된다(현행도 선택이다).
 *
 * 합계는 고른 것만 더한다. 살 수 없는 상태면(판매예정·종료·신청기간 밖) 버튼 자리에
 * 그 문구를 그린다 — 가이드에 그 상태가 없어 `action-cancel` 로 잠근 모습을 쓴다.
 *
 * 주문은 ⑥ 단계다. 지금은 **담기만 실제로 동작**하고 바로결제는 장바구니로 보낸다.
 */
export function PdBuyBox({ p, priceDetail, pointEnabled }: {
  p: ProductDetail;
  priceDetail: boolean;
  /** 마켓이 포인트를 쓰나 — 혜택 카드의 '함께쓰기' 문구가 이걸 본다 */
  pointEnabled: boolean;
}) {
  const t = useT();
  const lines = priceLines(p as never, priceDetail);
  const [picked, setPicked] = useState(0);
  const line = lines[picked] ?? lines[0];

  const books = p.childMappings
    .filter((m) => m.child.type === 'BOOK' && m.child.saleStatus === 'ON_SALE')
    .map((m) => m.child);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const toggle = (id: number) => setChecked((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /*
   * 혜택 유도 — 조건이 이 상품만으로 안 맞을 때 **같은 구성의 다른 대상**을 함께 담게 한다
   * (2026-08-24 사용자 지시). 체크한 것은 합계와 담기에 함께 들어간다.
   */
  const benefits = p.benefits ?? [];
  const [withIds, setWithIds] = useState<Set<number>>(new Set());
  const toggleWith = (id: number) => setWithIds((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  /** 체크한 관련 상품 — 같은 id 가 여러 구성에 있을 수 있어 한 번만 담는다 */
  const withTargets = (() => {
    const seen = new Map<number, ReturnType<typeof pickTarget>>();
    for (const b of benefits) {
      for (const tg of b.targets) {
        if (tg.self || !withIds.has(tg.id) || seen.has(tg.id)) continue;
        /*
         * **그 혜택의 채널 단가로 담는다**(2026-08-24 사용자 제보 — 모바일 단가 혜택인데
         * 전체 단가로 담겨 할인이 안 걸렸다). 목록에 적은 값과 담기는 값도 이래야 같다.
         */
        seen.set(tg.id, pickTarget(tg, priceDetail, b.apply?.platform ?? null));
      }
    }
    return [...seen.values()];
  })();

  const pickedBooks = books.filter((b) => checked.has(b.id));
  const gross = (line?.price ?? 0)
    + pickedBooks.reduce((a, b) => a + b.price, 0)
    + withTargets.reduce((a, x) => a + x.price, 0);

  /*
   * 지금 담긴 것으로 혜택 조건을 다시 센다 — **엔진과 같은 규칙**(개수는 상품 종류 수).
   * 여기 값은 **보여 주기 위한 것**이고 진짜 금액은 결제 때 `payment_validation` 이 정한다.
   */
  const cart: CartLine[] = [
    { productId: p.id, platform: line?.platform ?? 'ALL', price: line?.price ?? 0, quantity: 1, isBook: p.type === 'BOOK' },
    ...pickedBooks.map((b) => ({ productId: b.id, platform: 'ALL', price: b.price, quantity: 1, isBook: true })),
    ...withTargets.map((x) => ({ productId: x.id, platform: x.platform, price: x.price, quantity: 1, isBook: x.isBook })),
  ];
  const est = estimate(benefits, cart);
  /*
   * **이 상품 하나만** 담았을 때도 한 번 센다. 판정을 체크 포함으로 하면 체크한 순간
   * 안내가 사라져 되돌릴 수도 없다(2026-08-24 사용자 제보). 교재를 켰다 껐다 할 때
   * 툴팁이 깜박이지도 않아야 하므로 기준을 '자기 혼자' 로 고정한다.
   */
  const baseEst = estimate(benefits, [cart[0]]);
  /**
   * 혜택을 받으려면 함께 담아야 하는 상품 중 **이 페이지에 이미 체크박스로 있는 것**.
   * 그 자리(교재 줄)에 툴팁을 붙이고 '함께 담기' 카드에서는 뺀다(사용자 지시).
   */
  const bookIds = new Set(books.map((b) => b.id));
  const tipIds = (() => {
    const ids = new Set<number>();
    for (const tg of promotedTargets(benefits, baseEst.metKeys)) if (bookIds.has(tg.id)) ids.add(tg.id);
    return ids;
  })();
  /** 세 번째 묶음에 세울 대상 — 교재 줄에 말풍선으로 알린 것은 뺀다 */
  const picks = promotedTargets(benefits, baseEst.metKeys, bookIds);
  const total = Math.max(0, gross - est.discount);
  /*
   * 총액은 **깎이는 금액이 늘 때만** 굴러간다 — 혜택이 처음 붙을 때와, 혜택 상품을 더 담아
   * 할인이 커질 때다(2026-08-26). 단가를 고르거나 값만 달라지는 것은 조용히 바꾼다.
   */
  const shownTotal = useRolling(total, { from: gross, level: est.discount });
  /*
   * 할인 표기(2026-08-23 사용자 지시) — 가이드 `pd-price-discount` 자리다.
   * **가이드대로 기호 없이** 적는다(`10%`). 목록 카드는 현행 표기(`↓10%`)를 잇는데,
   * 둘은 같은 함수에서 나온다 — 다른 것은 기호뿐이라 값이 갈릴 일이 없다.
   */
  const money = t('prd.money');
  const courseDiscount = discountShort(p as never, money);
  const status = statusLabel(p as never, t);
  const [busy, setBusy] = useState(false);

  /** 담기 — 고른 단가와 체크한 교재를 함께 넣는다(회원이 고른 그대로) */
  const addCart = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const items = [
        { productId: p.id, platform: line?.platform },
        ...pickedBooks.map((b) => ({ productId: b.id })),
        // 혜택 조건을 채우려고 체크한 관련 상품도 함께 담는다
        ...withTargets.map((x) => ({ productId: x.id, platform: x.platform })),
      ];
      for (const it of items) {
        const res = await fetch('/api/members/me/cart', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(it),
        });
        if (res.status === 401) {
          window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
          return;
        }
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { message?: string };
          toast(body.message ?? t('prd.cartFailed'), 'error');
          return;
        }
      }
      toast(t('prd.addedToCart'));
      cartChanged();
    } catch {
      toast(t('acc.err.network'), 'error');
    } finally { setBusy(false); }
  };

  return (
    <>
      {/*
        혜택 카드 — **가격·결제 카드 위**(사용자 지시). 같은 컴포넌트 안에 두는 이유는
        관련 상품 체크가 곧 합계를 바꿔야 하기 때문이다(상태를 나눠 들면 못 굴린다).
      */}
      <BenefitNotices items={benefits} t={t} priceDetail={priceDetail} metKeys={est.activeKeys}
        product={{ coupon: p.couponEnabled, point: pointEnabled && p.pointEnabled }} marketPoint={pointEnabled} />

      <div className="pd-buybox">
      <div className="pd-buy-items">
        <div className="pd-buy-group-lbl">{t('prd.group.lecture')}</div>
        {lines.map((l, i) => (
          <div className="pd-buy-row" key={l.platform}>
            <label className="custom-check">
              <input type="radio" name={`pd-${p.id}`} checked={i === picked} onChange={() => setPicked(i)} />
              <span className="radio-box"></span>
              {/*
                단가 배지는 **세분화를 쓰는 마켓에서만**(2026-08-24 사용자 지시).
                안 쓰는 마켓은 줄이 하나뿐이라 '전체' 라고 적어도 가릴 것이 없다.
              */}
              {priceDetail && (
                <span className="badge badge-light item-type">{platformLabel(l.platform, t)}</span>
              )}
              <span className="item-name">{p.name}</span>
            </label>
            <div className="pd-buy-price">
              {l.original > l.price && <span className="pd-price-origin">{won(l.original)}{money}</span>}
              {courseDiscount && <span className="pd-price-discount">{courseDiscount}</span>}
              {l.price <= 0 ? t('prd.free') : `${won(l.price)}${money}`}
            </div>
          </div>
        ))}

        {books.length > 0 && (
          <>
            <div className="pd-buy-divider"></div>
            <div className="pd-buy-group-lbl">{t('prd.group.book')}</div>
            {books.map((b) => (
              <div className="pd-buy-row" key={b.id}>
                <label className="custom-check">
                  <input type="checkbox" checked={checked.has(b.id)} onChange={() => toggle(b.id)} />
                  <span className="check-box"></span>
                  <span className="item-name">{b.name}</span>
                  {/* 이 교재가 혜택 대상이면 그 자리에서 알린다(목록에 또 싣지 않는다) */}
                  {tipIds.has(b.id) && (
                    <span className="pd-buy-tip">{t('prd.benefit.addMore')}</span>
                  )}
                </label>
                <div className="pd-buy-price">
                  {b.priceOriginal > b.price && <span className="pd-price-origin">{won(b.priceOriginal)}{money}</span>}
                  {discountShort(b as never, money)
                    && <span className="pd-price-discount">{discountShort(b as never, money)}</span>}
                  {b.price <= 0 ? t('prd.free') : `${won(b.price)}${money}`}
                </div>
              </div>
            ))}
          </>
        )}

        {/* 세 번째 묶음 — 함께 담으면 혜택을 받는 상품(2026-08-24 사용자 제안 구조) */}
        <BenefitPicks picks={picks} checked={withIds} onToggle={toggleWith}
          priceDetail={priceDetail} t={t} />
      </div>

      <div className="pd-buy-summary">
        <div className="pd-total-lbl">{t('prd.total')}</div>
        <div className={`pd-total-val${est.discount > 0 ? ' is-cut' : ''}`}>
          {shownTotal <= 0 ? t('prd.free') : `${won(shownTotal)}${t('prd.money')}`}
        </div>
        {/*
          이 칸은 **금액만** 적는다(2026-08-24 사용자 지적) — 증정·쿠폰 같은 말은
          성분이 다르다. 그것은 위 혜택 카드가 이미 말한다.
        */}
        {est.discount > 0 && (
          <p className="pd-total-cut">{t('prd.benefit.cut', { v: won(est.discount) })}</p>
        )}
        <div className="pd-buy-btns">
          {status || !buyable(p as never) ? (
            <button className="action-btn action-cancel btn-lg" disabled>
              {status?.text ?? blockedLabel(p as never, t) ?? t('prd.notBuyable')}
            </button>
          ) : (
            <>
              {/* 가이드는 아이콘 없이 '장바구니' 다 — 글자도 아이콘도 임의로 더하지 않는다 */}
              <button className="action-btn action-danger btn-lg" disabled={busy} onClick={() => void addCart()}>
                {busy && <Working inline />}{t('prd.cart')}
              </button>
              {/*
                바로구매 — **장바구니를 거치지 않고 결제 화면으로**(2026-08-25 사용자 지시,
                목록의 `수강신청` 과 같은 길). 지금 화면에서 고른 것을 그대로 넘긴다 —
                고른 단가, 체크한 교재, 혜택 조건을 채우려고 체크한 관련 상품까지.
              */}
              <SmartLink className="action-btn action-submit btn-lg"
                href={`/checkout?buy=${cart.map((c) => `${c.productId}:${c.quantity}:${c.platform}`).join(',')}`}>
                {t('prd.buyNow')}
              </SmartLink>
            </>
          )}
        </div>
      </div>
      </div>
    </>
  );
}

/** 관련 대상 한 줄 — 담길 때의 단가와 값(프론트 `priceLines` 와 같은 규칙) */
function pickTarget(
  tg: { id: number; type: string; price: number; pricePc: number; priceMobile: number; salePlatforms: string[] },
  priceDetail: boolean,
  want?: string | null,
) {
  const line = targetLine(tg as never, priceDetail, want);
  return { id: tg.id, platform: line.platform, price: line.price, isBook: tg.type === 'BOOK' };
}
